import type { MasterProductFamily } from 'apis/master-product-families';
import type { BulkUpsertFamilyRow, EditableFamilyRow, RowValidationError } from './editable-family-row.types';

let counter = 0;
function nextClientId(): string {
  counter += 1;
  return `family-row-${counter}`;
}

export function createEmptyRow(): EditableFamilyRow {
  return { clientId: nextClientId(), id: undefined, universalName: '', category: null };
}

export function mapFamilyToRow(family: MasterProductFamily): EditableFamilyRow {
  return {
    clientId: nextClientId(),
    id: family.id,
    universalName: family.universalName,
    category: { id: family.catalogCategoryId, title: '' },
  };
}

export function appendRow(rows: EditableFamilyRow[]): EditableFamilyRow[] {
  return [...rows, createEmptyRow()];
}

export function prependRow(rows: EditableFamilyRow[]): EditableFamilyRow[] {
  return [createEmptyRow(), ...rows];
}

export function insertRowAt(rows: EditableFamilyRow[], index: number, position: 'above' | 'below'): EditableFamilyRow[] {
  const at = position === 'above' ? index : index + 1;
  const next = [...rows];
  next.splice(at, 0, createEmptyRow());
  return next;
}

export function removeRowAt(rows: EditableFamilyRow[], index: number): EditableFamilyRow[] {
  return rows.filter((_, i) => i !== index);
}

export function isRowEmpty(row: EditableFamilyRow): boolean {
  return row.universalName === '' && row.category === null && row.id === undefined;
}

export function validateRows(rows: EditableFamilyRow[], original: EditableFamilyRow[] = []): RowValidationError[] {
  const originalByClientId = new Map(original.map((row) => [row.clientId, row]));
  const errors: RowValidationError[] = [];
  rows.forEach((row, rowIndex) => {
    if (isRowEmpty(row)) return;
    const pristine = originalByClientId.get(row.clientId);
    if (pristine && rowSignature(pristine) === rowSignature(row)) return;
    const messages: string[] = [];
    if (!row.universalName.trim()) messages.push('Universal Name is required');
    if (!row.category) messages.push('Category is required');
    if (messages.length) errors.push({ clientId: row.clientId, rowIndex, messages });
  });
  return errors;
}

function rowSignature({ clientId: _clientId, ...rest }: EditableFamilyRow): string {
  return JSON.stringify(rest);
}

export function buildBulkPayload(rows: EditableFamilyRow[], original: EditableFamilyRow[] = []): BulkUpsertFamilyRow[] {
  const originalByClientId = new Map(original.map((row) => [row.clientId, row]));
  return rows
    .filter((row) => !isRowEmpty(row) && row.universalName.trim() !== '' && row.category !== null)
    .filter((row) => {
      const pristine = originalByClientId.get(row.clientId);
      return !pristine || rowSignature(pristine) !== rowSignature(row);
    })
    .map((row) => {
      const payload: BulkUpsertFamilyRow = {
        universalName: row.universalName,
        catalogCategoryId: row.category!.id,
      };
      if (row.id !== undefined) payload.id = row.id;
      return payload;
    });
}

export function hasUnsavedChanges(current: EditableFamilyRow[], original: EditableFamilyRow[]): boolean {
  const strip = (rows: EditableFamilyRow[]) => JSON.stringify(rows.map(({ clientId: _clientId, ...rest }) => rest));
  return strip(current) !== strip(original);
}
